import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import {
  BaseThreadFragmentFragmentGQLType,
  GetEventLogsQueryGQLType,
  ThreadActionGQLType,
  EventLogActorFragmentFragmentGQLType,
  EventLogTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  EventLogFilter,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { basicStringSearch } from '~/packages/event-logs/helpers';
import { useGetActorFiltersQuery } from '~/packages/event-logs/queries/entityFilters.gql.generated';
import { EventLogExportDataRow } from '~/packages/event-logs/types';

// Constants
export const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [];

// Types
export type EntityMeta = EventLogActorFragmentFragmentGQLType;
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type ThreadData = BaseThreadFragmentFragmentGQLType;

// Exports
type EntityNameProps = EntityMeta;
export const EntityName = ({ id: _id }: EntityNameProps) => {
  // Not implemented
  return null;
};

type ThreadFeedItemProps = {
  thread: ThreadData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const ThreadFeedItem = ({
  thread,
  onMount: _onMount,
  onReady: _onReady,
  onThreadAction: _onThreadAction,
}: ThreadFeedItemProps) => {
  switch (thread.domain) {
    default: {
      // Not implemented
      return null;
    }
  }
};

type EventFeedItemProps = {
  log: EventLogData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const EventFeedItem = ({
  log,
  onMount: _onMount,
  onReady: _onReady,
  onThreadAction: _onThreadAction,
}: EventFeedItemProps) => {
  switch (log.__typename) {
    default: {
      // Not implemented
      return null;
    }
  }
};

export const EntityFilter = () => {
  const selectedIds = useEventLogFilters((s) => s.actorIds);
  const onSelect = useEventLogFilters((s) => s.setActorIds);

  const [searchString, setSearchString] = useState<string>('');

  const { user: loggedInUser } = useUserAuthOrThrow();

  const entityFiltersQuery = useGetActorFiltersQuery();
  if (entityFiltersQuery.error) {
    Sentry.captureException(entityFiltersQuery.error);
    return null;
  }
  if (!entityFiltersQuery.data?.user?.organization.users) return null;

  const orgUsers = entityFiltersQuery.data.user.organization.users;
  const entities: EventLogActorFragmentFragmentGQLType[] | undefined = _.uniqBy(
    orgUsers
      .map((entity) => ({
        id: entity.id,
        firstName: entity.firstName,
        lastName: entity.lastName,
        organization: {
          id: loggedInUser.organization.id,
          slug: loggedInUser.organization.slug,
          name: loggedInUser.organization.name,
        },
      }))
      .concat(loggedInUser),
    'id',
  );

  if (entities.length < 1) return null;

  const options = entities
    .map((entity) => {
      const firstName = _.upperFirst(entity.firstName);
      const lastName = _.upperFirst(entity.lastName);
      return {
        id: entity.id,
        textLabel: `${firstName} ${lastName}`,
        data: entity,
      };
    })
    .filter((option) => basicStringSearch(searchString, option.textLabel));

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="user" />}
      minWidth="9rem"
      noneLabel="Any Actor"
      singularLabel="Actor"
      pluralLabel="Actors"
      selectedIds={selectedIds}
      options={options}
      onClose={onSelect}
    />
  );
};

/* eslint-disable camelcase */
export const toExportDataRow = (
  dataRow: EventLogExportDataRow,
  log: EventLogData,
): EventLogExportDataRow => {
  switch (log.__typename) {
    default: {
      return dataRow;
    }
  }
};
/* eslint-enable camelcase */
